/**
 * Defines the color options for header transparency.
 */
type HeaderTransparencyColor = 'white' | 'black'

/**
 * Represents the state of header transparency.
 */
type HeaderTransparencyState = {
  active: boolean
  color?: HeaderTransparencyColor
  paused?: boolean
}

/**
 * A composable for managing header configuration and behavior.
 *
 * @returns {Object} An object containing header configuration methods and states.
 * @property {Ref<boolean>} isNavigating - Indicates if the application is currently navigating.
 * @property {Ref<boolean>} isPinned - Indicates if the header is pinned.
 * @property {Ref<boolean>} freezeAnimation - Controls header animation freezing.
 * @property {Ref<HeaderTransparencyState>} transparencyState - Current header transparency state.
 * @property {Ref<Partial<HeaderTransparencyState>>} deferredTransparencyState - Deferred header transparency state.
 * @property {Ref<boolean>} megalogo - Controls mega logo visibility.
 *
 * @description
 * This composable provides comprehensive header configuration management:
 * - Handles header transparency and color
 * - Manages header pinning and navigation states
 * - Supports responsive design with breakpoint detection
 * - Implements dynamic header behavior based on page type and scroll position
 *
 * @example
 * const {
 *   isPinned,
 *   setHeaderTransparencyStatus,
 *   transparencyState
 * } = useDefaultHeaderConfig()
 *
 * @remarks
 * - Works only on the client-side
 * - Dynamically adjusts header based on route and scroll position
 * - Supports different behaviors for brand homepage, PLP, and PDP
 */
export const useDefaultHeaderConfig = () => {
  const { isBrandHome, isPlp, isPdp, isEditorialPage } = useRouteHelper()
  const { y } = useWindowScroll()
  const { breakpoint } = useProjectBreakpoint()
  const isMobile = breakpoint.smaller('lg')

  const isFederatedApp = computed(() => useRuntimeConfig().public.isFederated)

  const transparencyState = useState<HeaderTransparencyState>(
    'header-transparency',
    () => ({
      active: false,
    })
  )

  const deferredTransparencyState = useState<
    Omit<HeaderTransparencyState, 'active'> & { active?: boolean }
  >('header-deferred-transparency', () => ({
    active: undefined,
  }))

  const megalogo = useState<boolean>('header-megalogo', () => false)
  const isPinned = useState<boolean>('header-pinned', () => false)
  const isNavigating = useState<boolean>('header-navigating', () => false)
  const freezeAnimation = useState<boolean>(
    'header-freeze-animation',
    () => false
  )

  const isWatcherAlreadyAdded = useState<boolean>(
    'header-watcher-already-set',
    () => false
  )

  const setHeaderTransparencyStatus = (active: boolean) => {
    transparencyState.value.active = active

    if (typeof deferredTransparencyState.value.active === 'undefined') {
      deferredTransparencyState.value.active = active
    }
  }

  const setHeaderDeferredTransparencyStatus = (active: boolean) => {
    deferredTransparencyState.value.active = active
  }

  const setHeaderTransparencyColor = (color?: HeaderTransparencyColor) => {
    transparencyState.value.color = color
  }

  const setHeaderMegalogo = (isActive: boolean) => {
    megalogo.value = isActive
  }

  const setIsPinned = (pinned: boolean) => {
    isPinned.value = pinned
  }

  if (import.meta.client && !isWatcherAlreadyAdded.value) {
    isWatcherAlreadyAdded.value = true

    /*
     * Is Loading
     * this reference allows us to freeze the component state and update it once the page has finished loading
     */
    const nuxt = useNuxtApp()
    nuxt.hook('page:loading:start', () => {
      isNavigating.value = true
      freezeAnimation.value = true
    })

    nuxt.hook('page:finish', () => {
      isPinned.value = false
      isNavigating.value = false
    })

    nuxt.hook('page:loading:end', () => {
      const scrollTop =
        window.pageYOffset !== undefined
          ? window.pageYOffset
          : (
              document.documentElement ||
              document.body.parentNode ||
              document.body
            ).scrollTop

      isPinned.value = scrollTop > removePx(stripMaxHeight.value)

      setTimeout(() => {
        freezeAnimation.value = false
      }, 100)
    })

    /**
     * Pause header transparency based on the current page and scroll position
     */

    //BRAND HOMEPAGE: observe the CSS variable '--header-strip-wrapper'
    const stripMaxHeight = useCssVar('--header-strip-wrapper', null, {
      observe: true,
    })

    //PLP: get the height of the 'plp-hero'
    const plpHeroHeight = ref(0)
    const plpHeroStop = ref()
    const headerDefaultHeight = useCssVar('--header-default-height', null, {
      observe: true,
    })
    //set up a watcher to get the plp-hero element when navigating from a page where it does not exist
    watch(
      () => [isNavigating.value, isPlp.value],
      ([loading, plpPage]) => {
        if (!loading) {
          if (plpPage) {
            const { height, stop } = useElementSize(
              document.getElementById('plp-hero')
            )
            plpHeroStop.value = stop
            watchEffect(
              () =>
                (plpHeroHeight.value =
                  height.value +
                  removePx(stripMaxHeight.value) -
                  removePx(headerDefaultHeight.value))
            )
          } else {
            if (typeof plpHeroStop.value === 'function') plpHeroStop.value()
            plpHeroHeight.value = 0
          }
        }
      },
      {
        immediate: true,
      }
    )

    //PDP: calculate the distance of the last slide in the product image gallery
    const pdpLastSlideOffset = ref(0)
    const calcDistanceOfLastSlide = () => {
      const el = document.querySelector(
        '#pdp-main-block .gallery-slider .embla__container .embla__slide:last-of-type'
      )
      if (el) {
        pdpLastSlideOffset.value = el.getBoundingClientRect().top
      }
    }
    //set up a watcher to add or remove the event listener when navigating
    watch(
      () => isPdp.value,
      pdpPage => {
        if (pdpPage) {
          calcDistanceOfLastSlide()
          useEventListener('scroll', calcDistanceOfLastSlide, {
            passive: true,
          })
        } else {
          removeEventListener('scroll', calcDistanceOfLastSlide)
        }
      },
      {
        immediate: true,
      }
    )

    //put together the logic for BRAND HOMEPAGE, PLP and PDP
    watchEffect(() => {
      // TODO: (new heder) remove the sticky from the header when we are on the success page
      if (!isNavigating.value) {
        if (isFederatedApp.value) {
          isPinned.value = y.value > removePx(stripMaxHeight.value)
        } else if (isBrandHome.value || isEditorialPage.value) {
          const isSticky =
            y.value > removePx(stripMaxHeight.value) || isPinned.value

          transparencyState.value.paused = isSticky
        } else if (isPlp.value) {
          transparencyState.value.paused =
            y.value > Math.round(plpHeroHeight.value)
        } else if (isPdp.value) {
          transparencyState.value.paused = isMobile.value
            ? y.value > removePx(stripMaxHeight.value) + 3 // 3 is a offset value
            : pdpLastSlideOffset.value <= 0
        }
      }
    })

    //hanlde deferred states
    watch([isNavigating], () => {
      if (!isNavigating.value) {
        setHeaderDeferredTransparencyStatus(transparencyState.value.active)
      }
    })
  }

  return {
    isNavigating,
    isPinned,
    freezeAnimation,
    transparencyState,
    deferredTransparencyState,
    megalogo,
    setHeaderMegalogo,
    setIsPinned,
    setHeaderTransparencyStatus,
    setHeaderTransparencyColor,
  }
}
